import { useEffect, useState } from "react";
import { Box, Button, Checkbox, FormControlLabel, Typography } from "@mui/material";
import axios from "axios";
import { path } from "../../../routes";

// Zimní stadion Nymburk – hotový HLS stream od Comanetu. Jede rovnou do
// <video> na kiosku (hls.js), takže není potřeba ani serverový ffmpeg most,
// ani přímý RTSP, ani volba zdroje streamu.
const LIVE_STREAM = {
  key: "camera1",
  label: "Zimní stadion Nymburk",
  url: "https://wstream.comanet.cz:1943/live/Nymburk-zimak.stream/playlist.m3u8",
};

// ─── Zakomentováno: původní RTSP kamery (přímo z kamery na zimáku) ──────────
// Zůstává tu pro případ, že by se někdy vracelo k RTSP. Tehdy se vybírala
// kamera, ručně editovala URL a volil zdroj streamu (auto / přímý RTSP /
// serverový ffmpeg HLS most). Teď je zdroj jeden pevný HLS odkaz výše.
//
// const CAMERA_OPTIONS = [
//   { id: "cam1", key: "camera1", label: "Camera 1", url: "rtsp://admin:xZSx2026xNbk%21@89.190.32.75:554/stream1" },
//   { id: "cam2", key: "camera2", label: "Camera 2", url: "rtsp://admin:xZSx2026xNbk%21@89.190.32.75:554/stream2" },
// ];
//
// const normalizeRtspUrl = (u) => {
//   const s = String(u || "").trim();
//   if (!/^rtsp:\/\//i.test(s)) return s;
//   const m = s.match(/^rtsp:\/\/([^@\/]+)@(.+)$/i);
//   if (!m) return s;
//   const userinfo = m[1];
//   const hostPath = m[2];
//   const idx = userinfo.indexOf(":");
//   if (idx < 0) return s;
//   const user = userinfo.slice(0, idx);
//   const passRaw = userinfo.slice(idx + 1);
//   let pass = passRaw;
//   try { pass = decodeURIComponent(passRaw); } catch (_) {}
//   const enc = (v) => encodeURIComponent(v).replace(/[!'()*]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
//   return `rtsp://${enc(user)}:${enc(pass)}@${hostPath}`;
// };

const LiveCameras = ({ cred }) => {
  const [devices, setDevices] = useState([]);
  // Per-device persistent settings { [deviceId]: { liveCameraEnabled, liveCameraUrl, ... } }
  // Drží stav z DB; checkboxy z toho čtou, Save tuhle mapu propíše zpátky.
  const [deviceSettingsMap, setDeviceSettingsMap] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  // Displeje, na kterých má stream běžet.
  const selectedDeviceIds = devices
    .filter((d) => deviceSettingsMap[d.id]?.liveCameraEnabled === true)
    .map((d) => d.id);

  const getDeviceSettingsSafe = async (deviceId, orgId) => {
    try {
      const resp = await axios.get(`${path}/getDeviceSettings/${deviceId}?orgID=${encodeURIComponent(orgId)}`);
      return (resp.data?.settings && typeof resp.data.settings === "object") ? resp.data.settings : {};
    } catch (_) {
      return {};
    }
  };

  const fetchDevices = async () => {
    try {
      const orgId = cred?.cOrganization_id;
      const [kiosksRes, displaysRes] = await Promise.all([
        axios.get(`${path}/getDeviceV2?orgID=${orgId}`).catch(() => ({ data: [] })),
        axios.get(`${path}/getDisplaysbyOrg/${orgId}`).catch(() => ({ data: [] })),
      ]);
      const kiosks = Array.isArray(kiosksRes.data) ? kiosksRes.data : [];
      const displays = Array.isArray(displaysRes.data) ? displaysRes.data : [];
      const seen = new Set();
      const all = [...displays, ...kiosks].filter((d) => {
        const id = Number(d?.id);
        if (!Number.isFinite(id) || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      setDevices(all);

      setLoadingAssignments(true);
      const map = {};
      for (const d of all) {
        // eslint-disable-next-line no-await-in-loop
        map[d.id] = await getDeviceSettingsSafe(d.id, orgId);
      }
      setDeviceSettingsMap(map);
    } catch (e) {
      console.error("Failed to load devices for live cameras:", e);
      setDevices([]);
      setDeviceSettingsMap({});
    } finally {
      setLoadingAssignments(false);
    }
  };

  useEffect(() => {
    fetchDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cred?.cOrganization_id]);

  const toggleDevice = (id) => {
    setDeviceSettingsMap((prev) => {
      const cur = prev[id] || {};
      const isOn = cur?.liveCameraEnabled === true;
      return {
        ...prev,
        [id]: {
          ...cur,
          liveCameraEnabled: !isOn,
          liveCameraKey: !isOn ? LIVE_STREAM.key : null,
          liveCameraName: !isOn ? LIVE_STREAM.label : null,
          liveCameraUrl: !isOn ? LIVE_STREAM.url : null,
        },
      };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const orgId = cred?.cOrganization_id;
      // Save sequentially to avoid overloading backend (large Promise.all caused freezes).
      for (const d of devices) {
        const cur = deviceSettingsMap[d.id] || {};
        const isSelected = selectedDeviceIds.includes(d.id);
        const wasEnabled = Boolean(cur?.liveCameraEnabled);
        const hadCameraData = Boolean(cur?.liveCameraUrl || cur?.liveCameraKey);
        if (!isSelected && !wasEnabled && !hadCameraData) continue;
        const next = {
          ...cur,
          liveCameraEnabled: Boolean(isSelected),
          liveCameraUrl: isSelected ? LIVE_STREAM.url : null,
          liveCameraName: isSelected ? LIVE_STREAM.label : null,
          liveCameraKey: isSelected ? LIVE_STREAM.key : null,
          // HLS odkaz jde do <video> přímo — žádný přímý RTSP ani serverový most.
          liveCameraDirectRtsp: false,
          liveCameraForceServer: false,
        };
        // eslint-disable-next-line no-await-in-loop
        await axios.put(`${path}/setDeviceSettings/${d.id}`, { settings: next, orgID: orgId });
      }
      // Zakomentováno: start serverového RTSP→HLS mostu. Comanet stream je už
      // hotové HLS, takže se ffmpeg na serveru nespouští.
      // if (isRtsp && selectedDeviceIds.length > 0) {
      //   await axios.post(`${path}/live-camera/start`, { cameraKey: camera.key, rtspUrl: targetUrl }).catch(() => {});
      // }
      alert("Live stream assignment saved.");
      // Refresh, ať UI ukazuje aktuální stav z DB.
      await fetchDevices();
    } catch (e) {
      console.error("Failed to save live camera assignment:", e);
      alert("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const disableAll = async () => {
    setSaving(true);
    try {
      const orgId = cred?.cOrganization_id;
      for (const d of devices) {
        // eslint-disable-next-line no-await-in-loop
        const current = await getDeviceSettingsSafe(d.id, orgId);
        const next = {
          ...current,
          liveCameraEnabled: false,
          liveCameraUrl: null,
          liveCameraName: null,
          liveCameraKey: null,
          liveCameraDirectRtsp: false,
          liveCameraForceServer: false,
        };
        // eslint-disable-next-line no-await-in-loop
        await axios.put(`${path}/setDeviceSettings/${d.id}`, { settings: next, orgID: orgId });
      }
      // Úklid po starém RTSP režimu — kdyby na serveru zbyl běžící ffmpeg most.
      await Promise.allSettled([
        axios.post(`${path}/live-camera/stop`, { cameraKey: "camera1" }),
        axios.post(`${path}/live-camera/stop`, { cameraKey: "camera2" }),
      ]);
      alert("Live stream disabled on all devices.");
      await fetchDevices();
    } catch (e) {
      console.error("Failed to disable live cameras:", e);
      alert("Disable failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 1.25, sm: 2 },
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
        Live stream
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: "#666", lineHeight: 1.5 }}>
        Zaškrtni displeje, na kterých má běžet živý přenos ze zimního stadionu. Save zapne stream, Disable All ho všude vypne.
      </Typography>
      <Box
        sx={{
          mb: 2,
          p: { xs: 1, sm: 1.5 },
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          backgroundColor: "#fafafa",
          maxWidth: { xs: "100%", md: 900 },
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {LIVE_STREAM.label}
        </Typography>
        <Typography variant="caption" sx={{ color: "#666", wordBreak: "break-all", display: "block", mt: 0.25 }}>
          {LIVE_STREAM.url}
        </Typography>
      </Box>
      {/* ─── Zakomentováno: výběr kamery, ruční URL a volba zdroje streamu ────
          Patřilo k RTSP režimu (Camera 1 / Camera 2, auto / přímý RTSP / server
          ffmpeg most). Teď je zdroj jeden pevný HLS odkaz nahoře.

      <FormControl fullWidth sx={{ mb: 2, minWidth: 0, maxWidth: { xs: "100%", sm: 480, md: 560 } }}>
        <InputLabel id="live-camera-label">Camera</InputLabel>
        <Select labelId="live-camera-label" label="Camera" value={selectedCamera} onChange={(e) => setSelectedCamera(e.target.value)}>
          {CAMERA_OPTIONS.map((c) => (
            <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        fullWidth
        label="Stream URL (HLS/WebRTC preferred)"
        value={cameraUrl}
        onChange={(e) => setCameraUrl(e.target.value)}
        multiline
        minRows={2}
        helperText="Use external URL (e.g. https://.../index.m3u8). RTSP works only with server transcoding enabled."
      />
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="live-source-label">Stream source</InputLabel>
        <Select labelId="live-source-label" label="Stream source" value={sourceMode} onChange={(e) => setSourceMode(e.target.value)}>
          <MenuItem value="auto">Auto (webOS přímo, ostatní přes server)</MenuItem>
          <MenuItem value="direct">Přímý RTSP (LG webOS – odlehčí server)</MenuItem>
          <MenuItem value="server">Přes server (ffmpeg HLS most)</MenuItem>
        </Select>
      </FormControl>
      ─────────────────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          maxHeight: { xs: 280, sm: 420 },
          overflow: "auto",
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          p: { xs: 1, sm: 1.5 },
          mb: 2,
          WebkitOverflowScrolling: "touch",
        }}
      >
        {loadingAssignments ? (
          <Typography variant="body2" color="text.secondary">Loading saved state...</Typography>
        ) : devices.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No devices.</Typography>
        ) : (
          devices.map((d) => (
            <FormControlLabel
              key={d.id}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                ml: 0,
                mr: 0,
                mb: 0.5,
                "& .MuiCheckbox-root": { pt: 0.25 },
              }}
              control={
                <Checkbox
                  checked={selectedDeviceIds.includes(d.id)}
                  onChange={() => toggleDevice(d.id)}
                />
              }
              label={
                <Typography variant="body2" sx={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.4 }}>
                  {`${d.description || `Device ${d.id}`} (${Number(d.device_group) === 2 ? "Kiosk" : "Display"})`}
                </Typography>
              }
            />
          ))
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          flexWrap: "wrap",
          gap: { xs: 1, sm: 1.5 },
          "& .MuiButton-root": {
            width: { xs: "100%", sm: "auto" },
            minHeight: { xs: 44, sm: 36 },
          },
        }}
      >
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
        <Button variant="outlined" color="error" onClick={disableAll} disabled={saving}>
          Disable All
        </Button>
      </Box>
    </Box>
  );
};

export default LiveCameras;
